import { gql } from '@apollo/client';

import { InsightsFilterParams } from 'src/hooks/useInsightsFilter';
import { isDateWithinLastWeek } from 'src/lib/date/date';
import {
  CoordinateKind,
  FieldInsightsEmptyStateFragment,
} from 'src/lib/graphqlTypes/types';
import { isErrorsSort, isLatencySort } from 'src/lib/metrics';

export const fieldInsightsEmptyStateFragment = gql`
  fragment FieldInsightsEmptyStateFragment on GraphVariant {
    id
    routerLastSeenAt
    fieldUsageLastReportedAt
    fieldExecutionsLastReportedAt
    extendedRefsUsage {
      firstSeenAt
      lastSeenAt
    }
  }
`;

export enum FieldsEmptyStateReason {
  None,
  NoUsageInTimeRange,
  NoExecutionsReported,
  NoUsageReported,
  NoCoordinateUsageReported,
  ClientFiltersIncompatible,
  Filters,
}

export function getFieldsEmptyStateReason({
  routeParams,
  emptyStateVariantFragment,
  hasActiveFilters,
  hasActiveClientFilters,
}: {
  routeParams: InsightsFilterParams;
  emptyStateVariantFragment?: FieldInsightsEmptyStateFragment | null;
  hasActiveFilters?: boolean;
  hasActiveClientFilters?: boolean;
}): FieldsEmptyStateReason {
  if (!emptyStateVariantFragment) {
    return FieldsEmptyStateReason.None;
  }

  const fieldExecutionsLastReportedAt =
    emptyStateVariantFragment.fieldExecutionsLastReportedAt;
  const fieldUsageLastReportedAt =
    emptyStateVariantFragment.fieldUsageLastReportedAt;
  const extendedRefsLastSeenAt =
    emptyStateVariantFragment.extendedRefsUsage.lastSeenAt;
  const hasFieldUsage =
    !!fieldExecutionsLastReportedAt || !!fieldUsageLastReportedAt;
  const hasExtendedRefsEnabledThisWeek =
    !!extendedRefsLastSeenAt &&
    isDateWithinLastWeek(new Date(extendedRefsLastSeenAt));
  const isExtendedCoordinate =
    routeParams.coordinateKind &&
    [CoordinateKind.INPUT_OBJECT_FIELD, CoordinateKind.ENUM_VALUE].includes(
      routeParams.coordinateKind,
    );

  let emptyStateReason: FieldsEmptyStateReason =
    FieldsEmptyStateReason.NoUsageInTimeRange;

  if (
    (isLatencySort(routeParams.fieldsSort) ||
      isErrorsSort(routeParams.fieldsSort)) &&
    hasActiveClientFilters
  ) {
    // Show warning when we're sorting by latency or error AND filtering by client
    emptyStateReason = FieldsEmptyStateReason.ClientFiltersIncompatible;
  } else if (
    hasFieldUsage &&
    isExtendedCoordinate &&
    !hasExtendedRefsEnabledThisWeek
  ) {
    emptyStateReason = FieldsEmptyStateReason.NoCoordinateUsageReported;
  } else if (
    isLatencySort(routeParams.fieldsSort) &&
    !fieldExecutionsLastReportedAt &&
    fieldUsageLastReportedAt
  ) {
    // Show when we're sorting by latency AND no field executions reported
    emptyStateReason = FieldsEmptyStateReason.NoExecutionsReported;
  } else if (fieldExecutionsLastReportedAt && !fieldUsageLastReportedAt) {
    // Show when there are field executions reported but no field usage reported
    emptyStateReason = FieldsEmptyStateReason.NoUsageReported;
  } else if (hasActiveFilters) {
    // Show when there are active filters which could be hiding emptyStateVariantFragment
    emptyStateReason = FieldsEmptyStateReason.Filters;
  }
  return emptyStateReason;
}
